import { useAuth } from "@/modules/auth/use"
import { useAccount } from "@/modules/account/use"
import { useBootstrapFlatplan } from "@/modules/bootstrapFlatplan/use"
import { useBootstrapAccount } from "@/modules/bootstrapAccount/use"
import { useBootstrapApp } from "@/modules/bootstrapApp/use"
import { useBootstrapShare } from "@/modules/bootstrapShare/use"

export default (router) => {
  router.beforeEach(async (to) => {
    if (to.matched.some((record) => record.meta.bootstrapApp) || to.matched.some((record) => record.meta.bootstrapShare) || to.matched.some((record) => record.meta.bootstrapAccount)) {
      let bootstrap = bootstrapApp // default

      if (to.matched.some((record) => record.meta.bootstrapShare)) {
        bootstrap = bootstrapShare // use this instead
      }

      if ((await bootstrap(to)) === false) {
        return false
      }

      let bp_account = useAccount()
      const accountId = bp_account.currentAccountId.value

      if (to.matched.some((record) => record.meta.bootstrapFlatplan)) {
        let bp_bootstrapFlatplan = useBootstrapFlatplan()

        const flatplanId = to.params.flatplanId || bp_bootstrapFlatplan.lastFlatplanId(accountId)
        return accountRedirect(accountId, { mustBeActive: true }) || bootstrapFlatplan(accountId, flatplanId, to)
      } else if (to.matched.some((record) => record.meta.bootstrapAccount)) {
        return accountRedirect(accountId) || bootstrapAccount(accountId)
      }
    }
  })
}

// bootstrapApp and bootstrapShare resolve to false, which cancels the navigation, when the user has no accounts.
export const bootstrapApp = () => {
  let bp_bootstrapApp = useBootstrapApp()
  let bp_account = useAccount()
  let bp_auth = useAuth()

  return bp_bootstrapApp.fetch().then(() => {
    if (!bp_account.anyAccounts.value) {
      bp_auth.logout({ loggedOutReason: "There are no accounts linked to this user." })
      return false
    }
  })
}

export const bootstrapShare = (to) => {
  let bp_bootstrapShare = useBootstrapShare()
  let bp_account = useAccount()

  return bp_bootstrapShare.fetch(to.params.accountId).then(() => {
    if (!bp_account.anyAccounts.value) {
      return false
    }
  })
}

// Resolves to nothing: a guard that returns an object redirects to it, so the fetched response must not leak out.
export const bootstrapAccount = async (accountId) => {
  let bp_bootstrapAccount = useBootstrapAccount()
  await bp_bootstrapAccount.fetch(accountId)
}

// Resolves to where the navigation goes instead, or to nothing once the flatplan is loaded.
export const bootstrapFlatplan = async (accountId, flatplanId, to) => {
  // if we don't have a flatplanId by here - it's fine, the data API will find a flatplan for us, or even create one for us.
  let bp_bootstrapFlatplan = useBootstrapFlatplan()

  try {
    await bp_bootstrapFlatplan.init({
      accountId,
      flatplanId,
    })
  } catch (error) {
    if (error.response) {
      if (!to.params.flatplanId && flatplanId && error.response.status === 404) {
        // we might come here if the url params don't include a flatplanId,
        // so we then took it from localStorage. But if we the API returns with
        // a 404 - this could be if the flatplan from localstorage no longer exists.
        // In this case - delete localStorage, and try again.
        // The accountId - if we have one - should always be valid. See the getter
        // to get the default accountId.
        // let's delete the localStorage, else we end up in an infinite loop
        // if we then redirect
        bp_bootstrapFlatplan.forgetLastFlatplan({ accountId })

        // let's try again, but without specifiying a flatplanId
        return { name: "editor" }
      }

      // we have put a special meta field _ignore404 onto the request
      // so we can deal with the retry logic here (with the meta tag
      // we remove the 404 error handling from the axios interceptor)
      // But therefore we need to deal with the 404 here then.
      if (error.response.status === 404) {
        return { name: "404" }
      }

      console.error(error)
    }

    throw error
  }
}

// Where to send a navigation whose account can't be used, or nothing when it can. Throws when there's no account at all.
export const accountRedirect = (accountId, { mustBeActive = false } = {}) => {
  if (!accountId) {
    // need to push to a route to inform that we don't have any accounts attached to this user?
    throw new Error("No account")
  }

  let bp_account = useAccount()
  let account = bp_account.getAccountById(accountId)

  if (!account) {
    return { name: "404" }
  }

  if (mustBeActive && !account.active) {
    return { name: "billing" }
  }
}
